import type {
  ItemDecision,
  ItemState,
  PendingChange,
} from '@eluve/api-contract';
import { itemReviewPosture, needsVerdict } from '@eluve/api-contract';

type ReviewableItem = {
  itemState: ItemState;
  decision: ItemDecision;
  pendingChanges: PendingChange[];
};

/**
 * One logical row per item: a resource carrying several field proposals stays a
 * single row whose per-field diffs render in place, and whose verdict decides
 * every pending proposal together. The item's `decision` already aggregates its
 * proposals (pending while any change is undecided).
 */
export const confirmableRows = <T extends ReviewableItem>(items: T[]): T[] =>
  items.filter((item) => needsVerdict(itemReviewPosture(item)));

/** The proposal ids a row verdict must carry: the still-pending changes for
 *  confirm/reject; for undo, every decided one (rejected proposals and
 *  approved/applied ones — the server restores the latter from its
 *  applied-proposal memory). */
export const rowProposalIds = (
  item: Pick<ReviewableItem, 'pendingChanges'>,
  action: 'confirm' | 'reject' | 'undo',
): string[] =>
  item.pendingChanges
    .filter((change) =>
      action === 'undo'
        ? change.decision !== 'pending'
        : change.decision === 'pending',
    )
    .map((change) => change.proposalId);

/** The pending diff for one field of a grouped row, if any. */
export const changeByField = (
  item: Pick<ReviewableItem, 'pendingChanges'>,
  field: PendingChange['field'],
): PendingChange | null =>
  item.pendingChanges.find(
    (change) => change.field === field && change.decision === 'pending',
  ) ?? null;
